import axios from "axios";
import { API_URL } from "../../constants";

export interface CompanyUser {
  id: string;
  name?: string | null;
  email?: string | null;
  avatarFileId?: string | null;
}

// Permission values that grant access to the project module — mirrors the
// frontend route guard (components/protectedRoute/ProtectedWrap.js) and the
// nav gate (pages/app/dashboard/Menu.js). A user without one of these is bounced
// off the board by ProtectedRoute, so there's no point emailing them the link.
const PROJECT_ACCESS_PERMISSIONS = ["Master", "Access project"];

/**
 * Company users who can actually open the project module. Uses the existing
 * per-permission endpoint (GET /:companyId/users/fetch/:permission, which also
 * excludes disabled users) once per permission, then merges + dedupes by id
 * (a Master may match both). Sorted by display name.
 */
export const fetchProjectUsers = async (
  companyId: string
): Promise<CompanyUser[]> => {
  const lists = await Promise.all(
    PROJECT_ACCESS_PERMISSIONS.map((permission) =>
      axios
        .get(
          `${API_URL}/${companyId}/users/fetch/${encodeURIComponent(permission)}`
        )
        .then((res) => (res.data?.usersCollection || []) as CompanyUser[])
    )
  );
  const byId = new Map<string, CompanyUser>();
  lists.flat().forEach((u) => {
    if (u && u.id) byId.set(u.id, u);
  });
  return Array.from(byId.values()).sort((a, b) =>
    (a.name || a.email || "").localeCompare(b.name || b.email || "")
  );
};

/** Email one company user a link to the board. */
export const shareBoardWithUser = (
  companyId: string,
  boardId: string,
  userId: string
): Promise<{ sent: boolean }> => {
  const url = `${API_URL}/${companyId}/project-boards/${boardId}/share`;
  return axios.post(url, { userId }).then((res) => res.data);
};
